"use client"

import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import HoverBg from "src/components/ui/HoverBg";
import { useState } from "react";
import {
    Cable,
    Cloud,
    Cpu,
    Network,
    Server,
    Share2,
    Workflow,
} from "lucide-react";
import { Products } from "src/types/productsTypes";

const runtimeIconMap = {
    runtime: Server,
    orchestration: Workflow,
    network: Network,
    gpu: Cpu,
    cloud: Cloud,
    fabric: Cable,
    share: Share2,
} as const;

export default function BlockchainSupport({ section }: { section: Products['atlaside']['blockchainSupport'] }) {
    const [hovered, setHovered] = useState<string | null>(null);

    return (
        <section className="section-primary">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="title-size font-bold mb-6 bg-gradient-to-r from-purple-900 to-indigo-900 bg-clip-text text-transparent"
                    >
                        {section.title}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-size text-gray-600 max-w-3xl mx-auto"
                    >
                        {section.description}
                    </motion.p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-14">
                    {section.cards.map((runtime, index) => {
                        const key = `${runtime.name}-${index}` as const;
                        const Icon = runtime.icon ? runtimeIconMap[runtime.icon as keyof typeof runtimeIconMap] : null;

                        return (
                            <motion.div
                                key={runtime.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="group flex flex-col items-center text-center relative py-3"
                                onMouseEnter={() => setHovered(key)}
                                onMouseLeave={() => setHovered(null)}
                            >
                                {hovered === key && (
                                    <HoverBg
                                        layoutId="BlockchainSupport-hovered"
                                        className="rounded-2xl"
                                    />
                                )}

                                <div className="size-12 md:size-16 mb-4 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700">
                                    {Icon && <Icon className="size-6" />}
                                </div>

                                <div className="space-y-3">
                                    <h3 className="text-xl md:text-2xl font-bold text-gray-600">{runtime.name}</h3>
                                    <Badge
                                        variant="gray"
                                        className="text-[11px] md:text-xs text-white"
                                    >
                                        {runtime.tag}
                                    </Badge>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="text-center mt-12"
                >
                    <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-gray-700">
                            {section.comingSoonText}
                        </span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
